<div class="asset-logs-page">
  <!-- Top Header Banner -->
  <div class="logs-header-banner d-flex justify-content-between align-items-center flex-wrap">
    <div class="header-left">
      <div class="title-wrap d-flex align-items-center">
        <h1 class="page-title m-0">Asset Activity Logs</h1>
        <span class="count-pill ml-3" *ngIf="!isLoading">({{ totalCounts }})</span>
      </div>
      <p class="page-subtitle text-muted mt-1 mb-0">
        Audit trail and action logs across Inventory and Workstation assets
      </p>
    </div>

    <div class="header-right d-flex align-items-center mt-2 mt-sm-0">
      <button class="btn btn-refresh-main" (click)="fetchLogs()" [disabled]="isLoading" title="Refresh Logs">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" [class.spin]="isLoading">
          <path d="M23 4v6h-6"></path>
          <path d="M1 20v-6h6"></path>
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
        </svg>
        <span class="ml-1 d-none d-sm-inline">Refresh</span>
      </button>
    </div>
  </div>

  <!-- Unauthorized Warning Banner -->
  @if (isUnauthorized) {
    <div class="alert alert-danger unauthorized-card my-4" role="alert">
      <div class="d-flex align-items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-3 flex-shrink-0 text-danger">
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
          <line x1="12" y1="9" x2="12" y2="13"></line>
          <line x1="12" y1="17" x2="12.01" y2="17"></line>
        </svg>
        <div>
          <h5 class="alert-heading font-weight-bold mb-1">Access Restricted</h5>
          <p class="mb-0">{{ errorMessage }}</p>
          <p class="mb-0 small text-muted">Only users whose department is ADMIN or HR-HEAD are permitted to view asset logs.</p>
        </div>
      </div>
    </div>
  }

  <!-- Main Content if authorized -->
  @if (!isUnauthorized) {
    <!-- Filters Toolbar Card -->
    <div class="filter-card my-3">
      <!-- Row 1: Asset Type Selector & Search -->
      <div class="filter-row d-flex justify-content-between align-items-center flex-wrap gap-2 pb-2">
        <!-- Asset Type Tabs -->
        <div class="asset-type-tabs">
          <button
            type="button"
            class="tab-btn"
            [class.active]="assetTypeFilter === 'All'"
            (click)="onAssetTypeChange('All')">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            All Assets
          </button>
          <button
            type="button"
            class="tab-btn"
            [class.active]="assetTypeFilter === 'Inventory'"
            (click)="onAssetTypeChange('Inventory')">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
            </svg>
            Inventory
          </button>
          <button
            type="button"
            class="tab-btn"
            [class.active]="assetTypeFilter === 'Workstation'"
            (click)="onAssetTypeChange('Workstation')">
            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
              <line x1="8" y1="21" x2="16" y2="21"></line>
              <line x1="12" y1="17" x2="12" y2="21"></line>
            </svg>
            Workstation
          </button>
        </div>

        <!-- Search Input -->
        <div class="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="text"
            class="form-control search-input"
            placeholder="Search within page..."
            [(ngModel)]="searchKeyword" />
          @if (searchKeyword) {
            <button class="clear-search-btn" (click)="searchKeyword = ''">&times;</button>
          }
        </div>
      </div>

      <!-- Row 2: Date Filters & Presets -->
      <div class="filter-row d-flex justify-content-between align-items-center flex-wrap pt-2 border-top">
        <!-- Date Presets -->
        <div class="date-presets d-flex align-items-center flex-wrap gap-1">
          <span class="filter-label mr-2">Date:</span>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'all'"
            (click)="onDatePresetChange('all')">
            All Time
          </button>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'today'"
            (click)="onDatePresetChange('today')">
            Today
          </button>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'yesterday'"
            (click)="onDatePresetChange('yesterday')">
            Yesterday
          </button>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'last7'"
            (click)="onDatePresetChange('last7')">
            Last 7 Days
          </button>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'last30'"
            (click)="onDatePresetChange('last30')">
            Last 30 Days
          </button>
          <button
            type="button"
            class="preset-pill"
            [class.active]="selectedDatePreset === 'thisMonth'"
            (click)="onDatePresetChange('thisMonth')">
            This Month
          </button>
        </div>

        <!-- Custom Date Range Inputs -->
        <div class="custom-date-inputs d-flex align-items-center flex-wrap gap-2 mt-2 mt-md-0">
          <div class="date-input-wrap d-flex align-items-center">
            <label class="date-lbl mr-1">From:</label>
            <input
              type="date"
              class="form-control form-control-sm date-ctrl"
              [(ngModel)]="startDateInput"
              (change)="onCustomDateChange()" />
          </div>
          <div class="date-input-wrap d-flex align-items-center">
            <label class="date-lbl mr-1">To:</label>
            <input
              type="date"
              class="form-control form-control-sm date-ctrl"
              [(ngModel)]="endDateInput"
              (change)="onCustomDateChange()" />
          </div>

          @if (startDateInput || endDateInput || assetTypeFilter !== 'All') {
            <button class="btn btn-sm btn-clear-filter" (click)="resetFilters()" title="Reset all filters">
              Reset Filters
            </button>
          }
        </div>
      </div>
    </div>

    <!-- Error Banner if failed -->
    @if (errorMessage && !isUnauthorized) {
      <div class="alert alert-warning d-flex align-items-center my-3" role="alert">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <div>{{ errorMessage }}</div>
        <button class="btn btn-sm btn-outline-dark ml-auto" (click)="fetchLogs()">Retry</button>
      </div>
    }

    <!-- Loading Skeleton -->
    @if (isLoading) {
      <div class="table-loading-card text-center py-5">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
        <p class="text-muted mt-2 mb-0">Loading activity logs...</p>
      </div>
    }

    <!-- Empty State -->
    @if (!isLoading && filteredRecords.length === 0) {
      <div class="empty-state-card text-center py-5 my-3">
        <div class="empty-icon-box mb-3">
          <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14 2 14 8 20 8"></polyline>
            <line x1="16" y1="13" x2="8" y2="13"></line>
            <line x1="16" y1="17" x2="8" y2="17"></line>
            <polyline points="10 9 9 9 8 9"></polyline>
          </svg>
        </div>
        <h4 class="font-weight-bold mb-1">No Activity Logs Found</h4>
        <p class="text-muted mb-3">No activity matching your selected filters was recorded.</p>
        <button class="btn btn-primary btn-sm px-3" (click)="resetFilters()">
          Clear Filters
        </button>
      </div>
    }

    <!-- Table of Logs -->
    @if (!isLoading && filteredRecords.length > 0) {
      <div class="table-responsive logs-table-container">
        <table class="table logs-table mb-0">
          <thead>
            <tr>
              <th class="col-time">TIME (IST)</th>
              <th class="col-action">ACTION</th>
              <th class="col-asset">ASSET</th>
              <th class="col-actor">ACTOR</th>
              <th class="col-target">TARGET USER</th>
              <th class="col-summary">SUMMARY</th>
              <th class="col-more text-right">DETAILS</th>
            </tr>
          </thead>
          <tbody>
            @for (log of filteredRecords; track log._id) {
              <tr class="log-row" [class.row-expanded]="isRowExpanded(log._id)">
                <!-- 1. TIME (IST) -->
                <td class="col-time">
                  <div class="time-main font-weight-bold">
                    {{ log.createdAt | date:'d MMM y':'+0530' }}
                  </div>
                  <div class="time-sub text-muted small">
                    {{ log.createdAt | date:'h:mm a':'+0530' }} IST
                  </div>
                </td>

                <!-- 2. ACTION BADGE -->
                <td class="col-action">
                  <span class="action-pill" [ngClass]="getActionBadgeClass(log.action)">
                    {{ log.action }}
                  </span>
                </td>

                <!-- 3. ASSET (Name + Code + Type) -->
                <td class="col-asset">
                  <div class="asset-title-wrap d-flex align-items-center">
                    <span class="asset-type-badge mr-2" [ngClass]="log.assetType.toLowerCase()">
                      {{ log.assetType === 'Inventory' ? 'INV' : 'WS' }}
                    </span>
                    <span class="asset-name font-weight-bold" title="{{ log.assetName }}">{{ log.assetName }}</span>
                  </div>
                  @if (log.assetCode) {
                    <div class="asset-code-sub small text-muted mt-1">
                      <span class="code-tag">{{ log.assetCode }}</span>
                    </div>
                  }
                </td>

                <!-- 4. ACTOR (Avatar + Name) -->
                <td class="col-actor">
                  <div class="actor-cell d-flex align-items-center">
                    @if (log.actorProfileImg) {
                      <img [src]="log.actorProfileImg" class="actor-img mr-2" alt="{{ log.actorName }}" />
                    } @else {
                      <div class="actor-avatar-letter mr-2">
                        {{ getInitials(log.actorName) }}
                      </div>
                    }
                    <span class="actor-cell-name font-weight-medium">{{ log.actorName || 'System' }}</span>
                  </div>
                </td>

                <!-- 5. TARGET USER -->
                <td class="col-target">
                  @if (log.targetUserName) {
                    <div class="target-user-badge d-inline-flex align-items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="mr-1 text-primary">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                        <circle cx="12" cy="7" r="4"></circle>
                      </svg>
                      <span>{{ log.targetUserName }}</span>
                    </div>
                  } @else {
                    <span class="text-muted">—</span>
                  }
                </td>

                <!-- 6. SUMMARY -->
                <td class="col-summary">
                  <div class="summary-line text-truncate" title="{{ assetLogsService.buildMetaSummary(log) }}">
                    {{ assetLogsService.buildMetaSummary(log) }}
                  </div>
                </td>

                <!-- 7. DETAILS TOGGLE / BUTTON -->
                <td class="col-more text-right">
                  <button
                    class="btn btn-sm btn-icon-action"
                    (click)="toggleRowExpand(log._id)"
                    title="{{ isRowExpanded(log._id) ? 'Collapse details' : 'Expand details' }}">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" [class.rotated]="isRowExpanded(log._id)">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                  <button
                    class="btn btn-sm btn-outline-info ml-1 d-none d-lg-inline-flex align-items-center btn-inspect"
                    (click)="openRecordDetails(log)"
                    title="View Full Record Info">
                    Inspect
                  </button>
                </td>
              </tr>

              <!-- Expanded Details Row -->
              @if (isRowExpanded(log._id)) {
                <tr class="expanded-row">
                  <td colspan="7" class="p-0">
                    <div class="expanded-details-container p-3">
                      <!-- 1. Update Diffs -->
                      @if (log.action === 'Update' && getUpdatedFields(log).length > 0) {
                        <div class="meta-section mb-3">
                          <h6 class="meta-section-title">Field Updates</h6>
                          <div class="diff-table-wrap">
                            <table class="table table-sm diff-table mb-0">
                              <thead>
                                <tr>
                                  <th>Field</th>
                                  <th>Previous Value</th>
                                  <th>New Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                @for (item of getUpdatedFields(log); track item.field) {
                                  <tr>
                                    <td class="font-weight-bold">{{ item.field }}</td>
                                    <td class="diff-from"><span class="from-val">{{ formatValue(item.from) }}</span></td>
                                    <td class="diff-to"><span class="to-val">{{ formatValue(item.to) }}</span></td>
                                  </tr>
                                }
                              </tbody>
                            </table>
                          </div>
                        </div>
                      }

                      <!-- 2. Image Thumbnails -->
                      @if (getImages(log).length > 0) {
                        <div class="meta-section mb-3">
                          <h6 class="meta-section-title">Images ({{ getImages(log).length }})</h6>
                          <div class="image-grid d-flex flex-wrap gap-2">
                            @for (img of getImages(log); track img) {
                              <div class="img-thumbnail-item" (click)="openImagePreview(img)" title="Click to enlarge">
                                <img [src]="img" alt="Asset Image" class="thumb-img" />
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <!-- 3. Extend Action Dates -->
                      @if (log.action === 'Extend' && (log.meta?.['from'] || log.meta?.['to'])) {
                        <div class="meta-section mb-3">
                          <h6 class="meta-section-title">Date Extension</h6>
                          <div class="extend-dates-box d-flex align-items-center">
                            <span class="date-badge old-date">{{ (log.meta?.['from'] | date:'d MMM y') || 'N/A' }}</span>
                            <span class="arrow-sep mx-2">→</span>
                            <span class="date-badge new-date">{{ (log.meta?.['to'] | date:'d MMM y') || 'N/A' }}</span>
                          </div>
                        </div>
                      }

                      <!-- 4. Request Actions Special Information -->
                      @if (log.meta?.['requestType'] || log.meta?.['rejectionReason'] || log.meta?.['onHoldTill'] || log.meta?.['comment']) {
                        <div class="meta-section mb-3">
                          <h6 class="meta-section-title">Request Information</h6>
                          <div class="request-info-box p-2 rounded">
                            @if (log.meta?.['requestType']) {
                              <div class="req-item mb-1">
                                <span class="text-muted">Type:</span> <strong>{{ log.meta?.['requestType'] }}</strong>
                              </div>
                            }
                            @if (log.meta?.['rejectionReason']) {
                              <div class="req-item text-danger mb-1">
                                <span class="font-weight-bold">Rejection Reason:</span> {{ log.meta?.['rejectionReason'] }}
                              </div>
                            }
                            @if (log.meta?.['onHoldTill']) {
                              <div class="req-item text-warning mb-1">
                                <span class="font-weight-bold">On Hold Until:</span> {{ log.meta?.['onHoldTill'] | date:'d MMM y' }}
                              </div>
                            }
                            @if (log.meta?.['adminName'] || log.meta?.['adminId']) {
                              <div class="req-item mb-1">
                                <span class="text-muted">Forwarded To:</span> {{ log.meta?.['adminName'] || log.meta?.['adminId'] }}
                              </div>
                            }
                            @if (log.meta?.['maximumCost']) {
                              <div class="req-item mb-1">
                                <span class="text-muted">Maximum Cost:</span> ₹{{ log.meta?.['maximumCost'] }}
                              </div>
                            }
                            @if (log.meta?.['comment']) {
                              <div class="req-item mb-1">
                                <span class="text-muted">Comment:</span> {{ log.meta?.['comment'] }}
                              </div>
                            }
                            @if (log.meta?.['decidedBy']) {
                              <div class="req-item">
                                <span class="text-muted">Decided By:</span> {{ log.meta?.['decidedBy'] }}
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <!-- 5. Generic Meta Key/Value fallback -->
                      @if (hasExtraMeta(log)) {
                        <div class="meta-section">
                          <h6 class="meta-section-title">Additional Metadata</h6>
                          <div class="generic-meta-grid">
                            @for (entry of assetLogsService.getGenericMetaEntries(log.meta, log.action); track entry.key) {
                              <div class="meta-pair">
                                <span class="meta-key">{{ entry.key }}:</span>
                                <span class="meta-value">{{ formatValue(entry.value) }}</span>
                              </div>
                            }
                          </div>
                        </div>
                      }
                    </div>
                  </td>
                </tr>
              }
            }
          </tbody>
        </table>
      </div>

      <!-- Pagination Footer -->
      <div class="pagination-footer d-flex justify-content-between align-items-center flex-wrap gap-2 my-3 px-2">
        <!-- Page size selector -->
        <div class="page-size-wrap d-flex align-items-center">
          <span class="small text-muted mr-2">Rows per page:</span>
          <select class="form-control form-control-sm page-size-select" [ngModel]="limit" (ngModelChange)="onLimitChange($event)">
            @for (opt of limitOptions; track opt) {
              <option [value]="opt">{{ opt }}</option>
            }
          </select>
          <span class="small text-muted ml-3">
            Showing {{ (page - 1) * limit + 1 }} - {{ Math.min(page * limit, totalCounts) }} of {{ totalCounts }}
          </span>
        </div>

        <!-- Navigation Buttons -->
        <div class="page-nav-wrap d-flex align-items-center gap-1">
          <button class="btn btn-sm btn-nav" [disabled]="page <= 1" (click)="onPageChange(1)" title="First page">
            «
          </button>
          <button class="btn btn-sm btn-nav" [disabled]="page <= 1" (click)="onPageChange(page - 1)" title="Previous page">
            ‹
          </button>
          <span class="current-page-badge px-2">
            Page {{ page }} of {{ totalPages }}
          </span>
          <button class="btn btn-sm btn-nav" [disabled]="page >= totalPages" (click)="onPageChange(page + 1)" title="Next page">
            ›
          </button>
          <button class="btn btn-sm btn-nav" [disabled]="page >= totalPages" (click)="onPageChange(totalPages)" title="Last page">
            »
          </button>
        </div>
      </div>
    }
  }

  <!-- Lightbox Modal for Image Preview -->
  @if (selectedPreviewImage) {
    <div class="lightbox-overlay" (click)="closeImagePreview()">
      <div class="lightbox-content" (click)="$event.stopPropagation()">
        <button class="lightbox-close-btn" (click)="closeImagePreview()" title="Close">
          &times;
        </button>
        <img [src]="selectedPreviewImage" alt="Asset Image" class="lightbox-img" />
      </div>
    </div>
  }

  <!-- Inspect Modal / Slide-Over for full record view -->
  @if (selectedRecordForDetails) {
    <div class="modal-backdrop fade show"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" (click)="closeRecordDetails()">
      <div class="modal-dialog modal-dialog-centered modal-lg" role="document" (click)="$event.stopPropagation()">
        <div class="modal-content inspect-modal-content">
          <div class="modal-header d-flex justify-content-between align-items-center">
            <h5 class="modal-title font-weight-bold d-flex align-items-center">
              <span class="action-pill mr-2" [ngClass]="getActionBadgeClass(selectedRecordForDetails.action)">
                {{ selectedRecordForDetails.action }}
              </span>
              Log Details
            </h5>
            <button type="button" class="close" (click)="closeRecordDetails()">
              <span>&times;</span>
            </button>
          </div>
          <div class="modal-body">
            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Asset</label>
                <div class="font-weight-bold">{{ selectedRecordForDetails.assetName }}</div>
                <div class="small text-muted">{{ selectedRecordForDetails.assetType }} ({{ selectedRecordForDetails.assetCode || selectedRecordForDetails.assetId }})</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Timestamp (IST)</label>
                <div class="font-weight-bold">{{ selectedRecordForDetails.createdAt | date:'d MMMM y, h:mm:ss a':'+0530' }} IST</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Actor</label>
                <div class="font-weight-bold">{{ selectedRecordForDetails.actorName }}</div>
                <div class="small text-muted">ID: {{ selectedRecordForDetails.actorId }}</div>
              </div>
              <div class="col-md-6 mb-3">
                <label class="text-muted small">Target User</label>
                <div class="font-weight-bold">{{ selectedRecordForDetails.targetUserName || '—' }}</div>
                @if (selectedRecordForDetails.targetUserId) {
                  <div class="small text-muted">ID: {{ selectedRecordForDetails.targetUserId }}</div>
                }
              </div>
            </div>

            <hr />

            <h6 class="font-weight-bold mb-2">Meta Content</h6>
            <pre class="meta-json-box p-3 rounded"><code>{{ selectedRecordForDetails.meta | json }}</code></pre>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" (click)="closeRecordDetails()">Close</button>
          </div>
        </div>
      </div>
    </div>
  }
</div>
